// Core
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

// Chakra
import {
  Text,
  useColorModeValue,
  Divider,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  InputGroup,
  InputLeftElement,
  Input,
  FormControl,
  Menu,
  MenuButton,
  Button,
  MenuList,
  Box,
  MenuItem,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import { filterDate, parseDateTime } from '../../../services/Utils';
import UserPurchasesStats from './components/UserPurchasesStats';
import { useDebounce } from 'use-debounce';
import { SearchIcon } from '@chakra-ui/icons';
import usePermissionStatusByKey from '../../../hooks/usePermissionStatusByKey';
import {
  DateTimeIcon,
  DocumentIcon,
  FilterIcon,
} from '../../../components/Icons/Icons';
import DatePicker from 'react-datepicker';
import Errors from '../../../components/Errors';
import Success from '../../../components/Success';

const purchaseTypeOptions = {
  '': 'All Types',
  FUEL: 'Fuel',
  RETAIL: 'Retail',
};

const UserPurchase = () => {
  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [userPurchases, setUserPurchases] = useState([]);
  const [userPurchasesMeta, setUserPurchasesMeta] = useState([]);
  const limit = 25;

  const [search, setSearch] = useState();
  const [debouncedSearch] = useDebounce(search, 500);

  const isUserPurchases = usePermissionStatusByKey('GET:/admin/user-purchases');
  const isUserPurchasesStats = usePermissionStatusByKey(
    'GET:/admin/user-purchases/stats',
  );
  const isUserPurchase = usePermissionStatusByKey(
    'GET:/admin/user-purchases/:id',
  );

  const isUserPurchasesExport = usePermissionStatusByKey(
    'GET:/admin/user-purchases/export',
  );

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [filterButtonLabel, setFilterButtonLabel] = useState('All Time');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [purchaseType, setPurchaseType] = useState('');
  const handleDate = (dates) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);
  };

  const handleDateButton = (type) => {
    setFilterButtonLabel(type);

    const dates = filterDate(type);
    handleDate(dates);
  };

  const handlePurchaseType = (type) => {
    setPurchaseType(type);
  };

  const { mutate: exportCsv, isLoading: isExporting } = useMutation({
    mutationFn: async () => {
      const params = {};

      if (debouncedSearch) {
        params.userCid = debouncedSearch;
      }

      if (purchaseType) {
        params.type = purchaseType;
      }

      if (startDate) {
        params.from = new Date(startDate).toISOString();
      }

      if (endDate) {
        params.to = new Date(endDate).toISOString();
      }

      return apiService.exportCSVUserPurchases(params);
    },
    onSuccess: (csv) => {
      if (csv?.error) {
        setMessage(null);
        setError(csv.message);
        return;
      }

      setError(null);
      setMessage(true);

      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8',
      });

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'user-purchases.csv';
      a.click();

      URL.revokeObjectURL(url);

      setTimeout(() => {
        setMessage(null);
      }, 5000);
    },
  });

  const { isFetching, refetch } = useQuery({
    queryKey: [
      'user-purchases',
      offset,
      debouncedSearch,
      purchaseType,
      startDate,
      endDate,
      isUserPurchases,
    ],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (isUserPurchases) {
        const params = {
          limit,
          offset,
        };

        if (search) {
          params.userCid = search;
        }

        if (purchaseType) {
          params.type = purchaseType;
        }

        if (startDate) {
          params.from = new Date(startDate).toISOString();
        }

        if (endDate) {
          params.to = new Date(endDate).toISOString();
        }

        const response = await apiService.getUserPurchases(params);
        setUserPurchases(response.data);
        setUserPurchasesMeta(response.meta);
      }
      return true;
    },
    retry: false,
    enabled: !!isUserPurchases,
    refetchInterval: 30000,
  });

  const handleEditOpen = useCallback(
    (plan) => {
      history.push(`/user-purchases/${plan.id}`);
    },
    [history],
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text
          fontSize="40px"
          fontStyle="normal"
          fontWeight="700"
          color={textColor}
          mb="0px"
          className="page-name"
        >
          User Purchases
        </Text>
      </Flex>

      <Divider
        opacity="1"
        mr="2px"
        borderColor={borderColor}
        borderTopWidth="1px"
        borderBottomWidth="0"
        mb="32px"
      />

      {isUserPurchasesStats && <UserPurchasesStats />}

      {isUserPurchases && (
        <>
          <Flex
            gap={2}
            py={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex gap={2} alignItems="center">
              <FormControl
                variant="search"
                maxW="274px"
                className={search?.length > 0 ? 'search-active' : ''}
              >
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon fontSize="24px" ml="8px" />
                  </InputLeftElement>

                  <Input
                    type="text"
                    placeholder="User Cid"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              <Menu variant="filter">
                <MenuButton
                  as={Button}
                  leftIcon={<DateTimeIcon color="white" fontSize="24px" />}
                  variant="secondary-md"
                  color="white"
                  w="200px"
                  px="24px !important"
                >
                  {filterButtonLabel}
                </MenuButton>

                <MenuList className="context-menu filter-date">
                  <Box>
                    <DatePicker
                      onChange={handleDate}
                      selected={startDate}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      locale="en"
                      dateFormat="d/MM/yyyy"
                    />

                    {!startDate && !endDate && (
                      <Text
                        position="absolute"
                        variant="text"
                        className="filter-date-placeholder"
                        pointerEvents="none"
                      >
                        Enter a date range
                      </Text>
                    )}

                    <DateTimeIcon
                      fontSize="24px"
                      position="absolute"
                      right="20px"
                      top="17px"
                      color="#9FABCB"
                      pointerEvents="none"
                    />
                  </Box>

                  <MenuItem onClick={() => handleDateButton('All Time')}>
                    All Time
                  </MenuItem>
                  <MenuItem onClick={() => handleDateButton('This Month')}>
                    This Month
                  </MenuItem>
                  <MenuItem onClick={() => handleDateButton('Last Month')}>
                    Last Month
                  </MenuItem>
                  <MenuItem onClick={() => handleDateButton('Last 3 Month')}>
                    Last 3 Month
                  </MenuItem>
                  <MenuItem onClick={() => handleDateButton('This Year')}>
                    This Year
                  </MenuItem>
                </MenuList>
              </Menu>

              <Menu variant="filter">
                <MenuButton
                  as={Button}
                  leftIcon={<FilterIcon color="white" fontSize="24px" />}
                  variant="secondary-md"
                  color="white"
                  w="200px"
                  px="24px !important"
                >
                  {purchaseTypeOptions[purchaseType]}
                </MenuButton>

                <MenuList className="context-menu">
                  {Object.entries(purchaseTypeOptions).map(([value, label]) => (
                    <MenuItem
                      key={value}
                      onClick={() => handlePurchaseType(value)}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Flex>

            {isUserPurchasesExport && (
              <Button
                variant="secondary-sm"
                minW="150px"
                onClick={() => exportCsv()}
                isLoading={isExporting}
              >
                <DocumentIcon mr="1" />
                Export CSV
              </Button>
            )}
          </Flex>

          {message && <Success message={message} maxW="100%" />}
          {error && <Errors error={error} maxW="100%" />}

          {isFetching ? (
            <TableSkeleton rowCount={5} />
          ) : userPurchases.length > 0 ? (
            <Card padding={'8px'}>
              <Table color={textColor}>
                <Thead>
                  <Tr>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        User Cid
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Type
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Quantity
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Unit Price
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Gross Amount
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Discount Amount
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Net Amount
                      </Text>
                    </Td>
                    <Td>Purchased At</Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {userPurchases.map((row) => {
                    return (
                      <Tr
                        onClick={() => {
                          if (isUserPurchasesStats || isUserPurchase) {
                            handleEditOpen(row);
                          }
                        }}
                        _last={{
                          '& td': {
                            borderBottom: 'none !important',
                          },
                        }}
                        key={row.id}
                        border="unset"
                        className="cursor-tr"
                      >
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.userCid}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.type}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.quantity}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.unitPrice}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.grossAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.discountAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.netAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {parseDateTime(row?.purchasedAt)}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Card>
          ) : (
            <NotFound text="Not Found" />
          )}

          <Pagination
            meta={userPurchasesMeta ?? { limit, offset, total: 0 }}
            isLoadingPage={isFetching}
            currentPage={Math.floor(offset / limit) + 1}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </>
      )}

      {/*<Attention*/}
      {/*  variant="remove"*/}
      {/*  handle={() => handleDelete(object)}*/}
      {/*  isOpen={isOpen}*/}
      {/*  onOpen={onOpen}*/}
      {/*  onClose={onClose}*/}
      {/*/>*/}
    </>
  );
};

export default UserPurchase;
